import { scopeThreadRef } from "@amoreg/client-runtime/environment";
import type { EnvironmentId, ThreadId } from "@amoreg/contracts";
import { ChevronDownIcon, ScaleIcon } from "lucide-react";
import { memo, useMemo } from "react";

import { useComposerDraftStore, type DraftId } from "../composerDraftStore";
import { useThreadMetadata } from "../state/entities";
import {
  Menu,
  MenuCheckboxItem,
  MenuGroup,
  MenuGroupLabel,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "./ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { useActiveOrg, type OrgView } from "../state/orgs";
import { OrgLogoImage } from "./OrgSwitcher";
import { ComposerSpacePicker } from "./home/HomeSpaces";
import {
  mutateThreadSettings,
  readPendingDraftSettings,
  setPendingDraftSettings,
  useComposerContextSettings,
  type ThreadSettingsLoadState,
  type ThreadSettingsView,
} from "../state/threadSettings";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { cn } from "~/lib/utils";

interface BranchToolbarProps {
  environmentId: EnvironmentId;
  threadId: ThreadId;
  draftId?: DraftId;
}

/** The active org's brand mark at control scale (logo, or initial fallback). */
function TenantOrgMark({ org }: { readonly org: OrgView | null }) {
  return (
    <OrgLogoImage
      org={org}
      className="size-3 shrink-0 rounded-[3px] object-cover mx-0!"
      fallback={
        <span
          aria-hidden="true"
          className="flex size-3 shrink-0 items-center justify-center rounded-[3px] bg-muted text-[8px] font-semibold uppercase text-muted-foreground mx-0!"
        >
          {(org?.name ?? "?").slice(0, 1)}
        </span>
      }
    />
  );
}

/** The org's mark and name: the strip's "no space" label. */
function TenantOrgLabel({ org }: { readonly org: OrgView | null }) {
  return (
    <>
      <TenantOrgMark org={org} />
      <span className="min-w-0 truncate">{org?.name ?? "Organization"}</span>
    </>
  );
}

/**
 * The chat's sticky knowledge-base jurisdiction scope, at the right of the
 * strip under the prompt box. Same ghost-xs menu trigger and top-anchored
 * popup as the space picker at its left.
 */
export function TenantContextControls({
  threadId,
  settings,
  onMutate,
}: {
  readonly threadId: ThreadId;
  readonly settings: ThreadSettingsView;
  /** Drafts buffer changes locally; server threads POST directly. */
  readonly onMutate?: typeof mutateThreadSettings;
}) {
  const applySettings = onMutate ?? mutateThreadSettings;

  const selectedCount =
    settings.jurisdictionSlugs === null
      ? settings.orgJurisdictions.length
      : settings.jurisdictionSlugs.size;
  const jurisdictionLabel =
    settings.orgJurisdictions.length === 0
      ? "No jurisdictions"
      : settings.jurisdictionSlugs === null
        ? "All jurisdictions"
        : `${selectedCount}/${settings.orgJurisdictions.length} jurisdictions`;

  const toggleJurisdiction = (slug: string, checked: boolean) => {
    const current =
      settings.jurisdictionSlugs === null
        ? new Set(settings.orgJurisdictions.map((jurisdiction) => jurisdiction.slug))
        : new Set(settings.jurisdictionSlugs);
    if (checked) {
      current.add(slug);
    } else {
      current.delete(slug);
    }
    // Everything selected collapses back to the "all" default (null).
    const slugs = current.size === settings.orgJurisdictions.length ? null : [...current];
    void applySettings(threadId, { jurisdictionSlugs: slugs });
  };

  const jurisdictions =
    settings.orgJurisdictions.length === 0 ? (
      <span className="inline-flex h-7 min-w-0 shrink-0 items-center gap-1.5 rounded-md border border-transparent px-[calc(--spacing(2)-1px)] text-sm font-medium text-muted-foreground/70 sm:h-6 ms-auto">
        <ScaleIcon className="size-3 shrink-0" />
        <span className="min-w-0 truncate">{jurisdictionLabel}</span>
      </span>
    ) : (
      <Menu>
        <MenuTrigger
          aria-label={`Jurisdictions: ${jurisdictionLabel}`}
          render={<Button variant="ghost" size="xs" />}
          className="min-w-0 justify-end text-muted-foreground/70 hover:text-foreground/80 ms-auto"
        >
          {/* The tooltip sits on the label, not the trigger, so the Escape that closes the menu never leaves one open to take the next Escape. */}
          <Tooltip>
            <TooltipTrigger render={<span className="flex min-w-0 items-center gap-[inherit]" />}>
              <ScaleIcon className="size-3 shrink-0" />
              <span className="min-w-0 truncate">{jurisdictionLabel}</span>
            </TooltipTrigger>
            <TooltipPopup side="top">
              Knowledge-base scope: eve searches only the ticked jurisdictions
            </TooltipPopup>
          </Tooltip>
          <ChevronDownIcon className="size-3 shrink-0 opacity-50" />
        </MenuTrigger>
        <MenuPopup align="end" side="top" className="w-64">
          <MenuGroup>
            <MenuGroupLabel>Knowledge base jurisdictions</MenuGroupLabel>
            {settings.orgJurisdictions.map((jurisdiction) => {
              const checked =
                settings.jurisdictionSlugs === null ||
                settings.jurisdictionSlugs.has(jurisdiction.slug);
              return (
                <MenuCheckboxItem
                  key={jurisdiction.slug}
                  checked={checked}
                  disabled={!settings.canSetJurisdictions}
                  onCheckedChange={(next) => toggleJurisdiction(jurisdiction.slug, next)}
                >
                  <span className="flex min-w-0 items-center gap-1.5">
                    {jurisdiction.avatarUrl ? (
                      <img
                        src={jurisdiction.avatarUrl}
                        alt=""
                        aria-hidden="true"
                        className="size-3 shrink-0 rounded-[3px] object-cover"
                      />
                    ) : (
                      <ScaleIcon className="size-3 shrink-0" />
                    )}
                    <span className="min-w-0 truncate">{jurisdiction.name}</span>
                  </span>
                </MenuCheckboxItem>
              );
            })}
          </MenuGroup>
          <MenuSeparator />
          <MenuGroup>
            <MenuGroupLabel className="font-normal text-muted-foreground">
              Unticking narrows retrieval; everything ticked searches all.
            </MenuGroupLabel>
          </MenuGroup>
        </MenuPopup>
      </Menu>
    );

  return jurisdictions;
}

/**
 * The jurisdiction control before its settings land: a skeleton. Once a load
 * fails the shimmer stops: a still placeholder next to the banner above the
 * composer reads as "didn't load", not "still loading".
 */
function TenantContextSkeleton({ load }: { readonly load: ThreadSettingsLoadState }) {
  const failed = load._tag === "failed";
  const bone = failed ? "animate-none opacity-60" : "";
  return (
    <span
      className="inline-flex h-7 shrink-0 items-center gap-1.5 px-2 sm:h-6 ms-auto"
      aria-busy={!failed}
      aria-label={failed ? "Thread context failed to load" : "Loading thread context"}
    >
      <Skeleton className={cn("size-3 rounded-full", bone)} />
      <Skeleton className={cn("h-3 w-24", bone)} />
    </span>
  );
}

/** The glass strip under a composer: the chat's space, or a review prompt's resting controls. */
export function TenantContextStrip({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "chat-composer-context-strip -mt-4 mx-auto flex w-[calc(100%-2.75rem)] max-w-[calc(48rem-2.75rem)] items-center gap-1 ps-1 pe-2 pt-5 pb-1",
        className,
      )}
      {...props}
    />
  );
}

/**
 * The strip under the prompt box: the chat's space (or the org) on the left,
 * its knowledge-base jurisdictions on the right. Renders nothing for a thread
 * with no tenant context. The space picker is real before the settings land
 * (the org is known the moment the route resolves); the jurisdictions hold a
 * skeleton until then, so the strip never jumps when they arrive, and a
 * failure raises ChatView's banner above the composer.
 */
export const BranchToolbar = memo(function BranchToolbar({
  environmentId,
  threadId,
  draftId,
  spaceInHeadline = false,
}: BranchToolbarProps & {
  /** The draft hero's headline already offers the space ("… in <space>?"). */
  readonly spaceInHeadline?: boolean;
}) {
  const threadRef = useMemo(
    () => scopeThreadRef(environmentId, threadId),
    [environmentId, threadId],
  );
  const draftThread = useComposerDraftStore((store) =>
    draftId ? store.getDraftSession(draftId) : store.getDraftThreadByRef(threadRef),
  );
  const serverThread = useThreadMetadata(threadRef, { waitForShell: draftThread !== null });
  const tenantOrg = useActiveOrg();
  const tenant = useComposerContextSettings(threadId, tenantOrg?.id ?? null);

  if (serverThread === null && draftThread === null) return null;
  const draft = serverThread === null;
  const space = spaceInHeadline ? null : (
    <ComposerSpacePicker threadId={threadId} draft={draft} orgLabel={<TenantOrgLabel org={tenantOrg} />} variant="strip" />
  );

  if (tenant.source === "thread") {
    return (
      <TenantContextStrip>
        {space}
        <TenantContextControls threadId={threadId} settings={tenant.settings} />
      </TenantContextStrip>
    );
  }
  if (tenantOrg && tenant.load._tag !== "ready") {
    return (
      <TenantContextStrip>
        {space}
        <TenantContextSkeleton load={tenant.load} />
      </TenantContextStrip>
    );
  }
  // Drafts are fully interactive: choices overlay the org defaults locally
  // (keyed by the draft's pre-allocated thread id) and flush to the server
  // the moment the first message creates the thread.
  if (tenantOrg && tenant.source === "org") {
    const pending = readPendingDraftSettings(threadId);
    return (
      <TenantContextStrip>
        {space}
        <TenantContextControls
          threadId={threadId}
          settings={{
            ...tenant.settings,
            jurisdictionSlugs:
              pending?.jurisdictionSlugs == null ? null : new Set(pending.jurisdictionSlugs),
            canSetJurisdictions: true,
          }}
          onMutate={(id, patch) => {
            setPendingDraftSettings(id, patch, tenantOrg.id);
            return Promise.resolve(true);
          }}
        />
      </TenantContextStrip>
    );
  }
  return null;
});
